'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useLanguage } from '@/context/LanguageContext';
import { getTranslations } from '@/lib/i18n';

export default function Footer() {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const kn = language === 'kn';

  const programs = [
  { label: kn ? 'ಶಿಕ್ಷಣ ಕಾರ್ಯಕ್ರಮ' : 'Education Program', href: '/programs' },
  { label: kn ? 'ವಿದ್ಯಾರ್ಥಿವೇತನ' : 'Scholarships', href: '/programs' },
  { label: kn ? 'ಶಿಕ್ಷಕ ತರಬೇತಿ' : 'Teacher Training', href: '/programs' },
  { label: kn ? 'ಸಮುದಾಯ ಆರೋಗ್ಯ' : 'Community Health', href: '/programs' },
  { label: kn ? 'ಮಹಿಳಾ ಸಬಲೀಕರಣ' : 'Women Empowerment', href: '/programs' }];


  const quickLinks = [
  { label: kn ? 'ನಮ್ಮ ಬಗ್ಗೆ' : 'About Us', href: '/about' },
  { label: kn ? 'ನಮ್ಮ ಪ್ರಭಾವ' : 'Our Impact', href: '/impact' },
  { label: kn ? 'ಕಾರ್ಯಕ್ರಮಗಳು' : 'Events', href: '/events' },
  { label: kn ? 'ಸುದ್ದಿ ಮತ್ತು ಕಥೆಗಳು' : 'News & Stories', href: '/news' },
  { label: kn ? 'ಸಂಪನ್ಮೂಲಗಳು' : 'Resources', href: '/resources' },
  { label: kn ? 'ಭಾಗವಹಿಸಿ' : 'Get Involved', href: '/get-involved' }];


  const legal = [
  { label: kn ? 'ಗೌಪ್ಯತಾ ನೀತಿ' : 'Privacy Policy', href: '/legal' },
  { label: kn ? 'ಬಳಕೆಯ ನಿಯಮಗಳು' : 'Terms of Use', href: '/legal' },
  { label: kn ? 'ದೇಣಿಗೆ ನೀತಿ' : 'Donation Policy', href: '/legal' }];


  return (
    <footer className="bg-indigo text-white">
      {/* CTA Banner */}
      <div className="bg-gradient-to-r from-saffron-dark via-saffron to-gold py-10 px-6">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className={`text-2xl font-bold text-white ${kn ? 'font-kannada' : ''}`}>
              {kn ? 'ಚಳುವಳಿಯಲ್ಲಿ ಸೇರಿ' : 'Join the Movement'}
            </h3>
            <p className={`text-white/80 mt-1 text-sm ${kn ? 'font-kannada' : ''}`}>
              {kn ? 'ಪ್ರತಿ ಕೊಡುಗೆ ಒಂದು ಜೀವನ ಪರಿವರ್ತಿಸುತ್ತದೆ.' : 'Every contribution transforms a life. Be the change today.'}
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Link href="/donate" className={`px-6 py-3 bg-white text-saffron font-bold rounded-full text-sm hover:bg-neutral-100 transition-colors ${kn ? 'font-kannada' : ''}`}>
              {t?.nav?.donateNow}
            </Link>
            <Link href="/get-involved" className={`px-6 py-3 bg-white/20 text-white font-semibold rounded-full text-sm border border-white/40 hover:bg-white/30 transition-colors ${kn ? 'font-kannada' : ''}`}>
              {t?.nav?.volunteer}
            </Link>
          </div>
        </div>
      </div>
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link href="/home" className="flex items-center gap-3 mb-5">
              <div className="relative w-12 h-12 rounded-full overflow-hidden bg-white ring-1 ring-saffron/40">
                <Image
                  src="/assets/images/make_a_3d_model_of_202605151251-removebg-preview-1778924347801.png"
                  alt="Arivu Foundation Logo"
                  fill
                  className="object-cover"
                  style={{ mixBlendMode: 'multiply' }} />

              </div>
              <div>
                <div className="font-bold text-lg text-white leading-tight">Arivu Foundation</div>
                <div className="text-xs text-white/60 font-kannada">ಅರಿವು ಫೌಂಡೇಶನ್</div>
              </div>
            </Link>
            <p className={`text-white/60 text-sm leading-relaxed mb-4 ${kn ? 'font-kannada' : ''}`}>
              {t?.footer?.tagline}
            </p>
            <p className="text-white/50 text-xs mb-5">
              Reg. No: TRUST_REG_NUMBER<br />
              {t?.footer?.certified} / {t?.footer?.taxExempt}<br />
              Bengaluru, Karnataka 560073
            </p>
            {/* Social */}
            <div className="flex gap-3">
              {['facebook', 'twitter', 'instagram', 'youtube']?.map((s) =>
              <a key={s} href="#" aria-label={s}
              className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-saffron transition-colors">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-white/70">
                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </a>
              )}
            </div>
          </div>

          {/* Programs */}
          <div>
            <h4 className={`font-bold text-white mb-5 text-sm uppercase tracking-wider ${kn ? 'font-kannada' : ''}`}>
              {t?.footer?.programs}
            </h4>
            <ul className="space-y-3">
              {programs?.map((p) =>
              <li key={p?.label}>
                  <Link href={p?.href} className={`text-white/60 text-sm hover:text-saffron-light transition-colors flex items-center gap-2 ${kn ? 'font-kannada' : ''}`}>
                    <span className="w-1 h-1 rounded-full bg-saffron flex-shrink-0"></span>
                    {p?.label}
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className={`font-bold text-white mb-5 text-sm uppercase tracking-wider ${kn ? 'font-kannada' : ''}`}>
              {t?.footer?.quickLinks}
            </h4>
            <ul className="space-y-3">
              {quickLinks?.map((l) =>
              <li key={l?.label}>
                  <Link href={l?.href} className={`text-white/60 text-sm hover:text-saffron-light transition-colors flex items-center gap-2 ${kn ? 'font-kannada' : ''}`}>
                    <span className="w-1 h-1 rounded-full bg-saffron flex-shrink-0"></span>
                    {l?.label}
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className={`font-bold text-white mb-5 text-sm uppercase tracking-wider ${kn ? 'font-kannada' : ''}`}>
              {t?.footer?.contact}
            </h4>
            <ul className="space-y-4 text-sm text-white/60">
              <li className="flex gap-3">
                <svg className="w-4 h-4 text-saffron flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Bengaluru, Karnataka<br />560073, India</span>
              </li>
              <li className="flex gap-3">
                <svg className="w-4 h-4 text-saffron flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <a href="tel:+919876543210" className="hover:text-saffron-light transition-colors">+91 7353101553</a>
              </li>
              <li className="flex gap-3">
                <svg className="w-4 h-4 text-saffron flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href="mailto:info@arivufoundation.org" className="hover:text-saffron-light transition-colors">info@arivufoundation.org</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className={`text-white/40 text-xs ${kn ? 'font-kannada' : ''}`}>
            © 2026 Arivu Foundation. {t?.footer?.rights} | {t?.footer?.trust}
          </p>
          <div className="flex gap-5">
            {legal?.map((l) =>
            <Link key={l?.label} href={l?.href} className={`text-white/40 text-xs hover:text-white/70 transition-colors ${kn ? 'font-kannada' : ''}`}>
                {l?.label}
              </Link>
            )}
          </div>
        </div>
      </div>
    </footer>);

}